// src/utils/imageCompression.js

/**
 * Compress image file before upload
 * @param {File} file - Image file to compress
 * @param {number} maxSizeMB - Maximum size in MB (default 2MB)
 * @param {number} maxWidthOrHeight - Maximum width or height (default 1920)
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (file, maxSizeMB = 2, maxWidthOrHeight = 1920) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.readAsDataURL(file);

        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;

            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                // Calculate new dimensions
                if (width > height) {
                    if (width > maxWidthOrHeight) {
                        height = height * (maxWidthOrHeight / width);
                        width = maxWidthOrHeight;
                    }
                } else {
                    if (height > maxWidthOrHeight) {
                        width = width * (maxWidthOrHeight / height);
                        height = maxWidthOrHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                // Compress with quality adjustment
                let quality = 0.9;
                const maxSizeBytes = maxSizeMB * 1024 * 1024;

                const compressRecursive = (currentQuality) => {
                    canvas.toBlob(
                        (blob) => {
                            if (blob.size > maxSizeBytes && currentQuality > 0.1) {
                                // Reduce quality and try again
                                compressRecursive(currentQuality - 0.1);
                            } else {
                                // Create new file from blob
                                const compressedFile = new File([blob], file.name, {
                                    type: file.type,
                                    lastModified: Date.now(),
                                });
                                resolve(compressedFile);
                            }
                        },
                        file.type,
                        currentQuality
                    );
                };

                compressRecursive(quality);
            };

            img.onerror = (error) => {
                reject(new Error('Failed to load image'));
            };
        };

        reader.onerror = (error) => {
            reject(new Error('Failed to read file'));
        };
    });
};

/**
 * Validate image file
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Maximum size in MB
 * @returns {Object} - { valid: boolean, error: string }
 */
export const validateImageFile = (file, maxSizeMB = 2) => {
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
        return {
            valid: false,
            error: 'File must be an image',
            errorAr: 'يجب أن يكون الملف صورة'
        };
    }

    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
        return {
            valid: false,
            error: `Image size must be less than ${maxSizeMB}MB`,
            errorAr: `حجم الصورة يجب أن يكون أقل من ${maxSizeMB} ميجابايت`
        };
    }

    return { valid: true };
};
